import { DELIVERABLE_KEY_MAP } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-configuration.store';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { camelCase, snakeCase, startCase } from 'lodash';
import { parseDate } from '../create-campaign-dialog/brief-wizard-dialog/brief-wizard-dialog.util';

const MILLISECONDS_IN_A_MINUTE = 60_000;

const DELIVERABLE_KEY_TYPE_MAP: Record<string, EDeliverableType> = DELIVERABLE_KEY_MAP;

// DELIVERABLE_KEY_MAP has two keys for chatBot ('chatbot', 'chatbots') and chatbotMulti
// ('chatbotMulti', 'chatbotMultis'); every other type has exactly one. Object.entries preserves
// declaration order, and the plural form is declared last in both pairs, so a last-write-wins
// reduce always lands on the plural - the array key the brief JSON (and every other deliverable
// array here) actually uses.
const DELIVERABLE_TYPE_KEY_MAP: Partial<Record<EDeliverableType, string>> = Object.entries(
  DELIVERABLE_KEY_TYPE_MAP,
).reduce((accumulator: Partial<Record<EDeliverableType, string>>, [key, type]) => {
  accumulator[type] = key;

  return accumulator;
}, {});

const NON_DELIVERABLE_CTA_TEMPLATE_KEYS = new Set([
  'id',
  'name',
  'platformFeature',
  'executions',
  'isAllCreators',
  'campaignCreators',
]);

export function deepCamelCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => deepCamelCaseKeys(item));
  }

  if (!value || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce(
    (accumulator: Record<string, unknown>, [key, nestedValue]) => {
      accumulator[camelCase(key)] = deepCamelCaseKeys(nestedValue);

      return accumulator;
    },
    {},
  );
}

export function deepSnakeCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => deepSnakeCaseKeys(item));
  }

  if (!value || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce(
    (accumulator: Record<string, unknown>, [key, nestedValue]) => {
      accumulator[snakeCase(key)] = deepSnakeCaseKeys(nestedValue);

      return accumulator;
    },
    {},
  );
}

// Tokenizes a ValidationMessage.path into a uniform key/index list. The API mixes two styles in
// the same errors array: pydantic's dotted-index style ("cta_templates.0.game_plays.0.duration")
// and the existing validators' bracket style ("cta_templates[1].executions[0].execution_date").
// Both become e.g. ['ctaTemplates', 0, 'gamePlays', 0, 'duration'].
export function tokenizePath(path: string): (string | number)[] {
  return path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((segment) => segment.length > 0)
    .map((segment) => (/^\d+$/.test(segment) ? Number(segment) : camelCase(segment)));
}

// Flattens a camelCased CTA template's per-deliverable-type arrays (gamePlays, titles, ...) into
// a single deliverables array with an explicit `type`, matching the shape ICtaForm/TDeliverableForm
// already expect everywhere else in the app.
export function flattenCtaTemplateDeliverables(ctaTemplate: Record<string, unknown>): Record<string, unknown> {
  const deliverables: Record<string, unknown>[] = [];
  const rest: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(ctaTemplate)) {
    const deliverableType = DELIVERABLE_KEY_TYPE_MAP[key];

    if (deliverableType && Array.isArray(value)) {
      deliverables.push(...value.map((item: Record<string, unknown>) => ({ ...item, type: deliverableType })));

      continue;
    }

    rest[key] = value;
  }

  return { ...rest, deliverables };
}

// The reverse of flattenCtaTemplateDeliverables: regroups a flat `deliverables` array back into
// the per-type arrays the API expects, dropping the `type` discriminator (structural again).
export function unflattenCtaTemplateDeliverables(ctaTemplate: Record<string, unknown>): Record<string, unknown> {
  const { deliverables, ...rest } = ctaTemplate as { deliverables?: Record<string, unknown>[] } & Record<
    string,
    unknown
  >;

  const grouped: Record<string, Record<string, unknown>[]> = {};

  for (const deliverable of deliverables ?? []) {
    const { type, ...fields } = deliverable;
    const arrayKey = DELIVERABLE_TYPE_KEY_MAP[type as EDeliverableType];

    if (!arrayKey) {
      continue;
    }

    grouped[arrayKey] = grouped[arrayKey] ?? [];
    grouped[arrayKey].push(fields);
  }

  return { ...rest, ...grouped };
}

// The backend's platform_feature string is PascalCase ("TwitchStream"); the app's enum values are
// camelCase ("twitchStream"). Every other enum-ish value the brief produces (executionType,
// currency) already matches the app's lowercase enum values as-is.
export function normalizePlatformFeatureValue(value: unknown): EPlatformFeature | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  return camelCase(value) as EPlatformFeature;
}

export function denormalizePlatformFeatureValue(value: EPlatformFeature | null): string | null {
  if (!value) {
    return null;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Converts one raw (already deep-camelCased) cta template into the deliverables-flattened,
// enum-normalized shape the wizard's step forms are built from.
export function denormalizeCtaTemplate(rawCtaTemplate: Record<string, unknown>): Record<string, unknown> {
  const flattened = flattenCtaTemplateDeliverables(rawCtaTemplate);

  return {
    ...flattened,
    platformFeature: normalizePlatformFeatureValue(flattened['platformFeature']),
  };
}

export function renormalizeCtaTemplate(ctaTemplate: Record<string, unknown>): Record<string, unknown> {
  const withDeliverables = unflattenCtaTemplateDeliverables(ctaTemplate);

  return {
    ...withDeliverables,
    platformFeature: denormalizePlatformFeatureValue(ctaTemplate['platformFeature'] as EPlatformFeature | null),
  };
}

// Converts the raw snake_case `campaign` dict from /brief/analyze or /brief/validate into the
// camelCase, deliverables-flattened working draft the wizard edits.
export function denormalizeCampaign(rawCampaign: Record<string, unknown>): Record<string, unknown> {
  const camelCased = deepCamelCaseKeys(rawCampaign) as Record<string, unknown>;
  const ctaTemplates = ((camelCased['ctaTemplates'] as Record<string, unknown>[]) ?? []).map(denormalizeCtaTemplate);

  return { ...camelCased, ctaTemplates };
}

// Keys the real campaign-form's FormGroups carry (database ids, custom-per-creator value
// bookkeeping) that the brief's own pydantic models never emit and never need back - the brief
// schema is the LLM-output shape ("if the brief didn't say it, write null"), not the full
// database-backed campaign schema. Stripped before /brief/validate so reusing those FormGroups
// here can't 422 the request over a field the brief side neither has nor cares about.
const UI_ONLY_KEYS = new Set(['id', 'isNew', 'customCtaValues', 'exclusiveTo', 'globalCtaValues']);

function stripUiOnlyKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => stripUiOnlyKeys(item));
  }

  if (!value || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce(
    (accumulator: Record<string, unknown>, [key, nestedValue]) => {
      if (!UI_ONLY_KEYS.has(key)) {
        accumulator[key] = stripUiOnlyKeys(nestedValue);
      }

      return accumulator;
    },
    {},
  );
}

// The reverse: converts the wizard's working draft back into the snake_case shape /brief/validate
// expects.
export function renormalizeCampaign(campaign: Record<string, unknown>): Record<string, unknown> {
  const ctaTemplates = ((campaign['ctaTemplates'] as Record<string, unknown>[]) ?? []).map(renormalizeCtaTemplate);

  return deepSnakeCaseKeys(stripUiOnlyKeys({ ...campaign, ctaTemplates })) as Record<string, unknown>;
}

// Durations go through Number(): the wizard's duration inputs are `type="text"`, so an edited one is a string.
export function getGamePlaysDuration(ctaTemplate: Record<string, unknown> | undefined): number {
  return ((ctaTemplate?.['deliverables'] as Record<string, unknown>[]) ?? [])
    .filter((deliverable) => deliverable['type'] === EDeliverableType.gamePlay)
    .reduce((total, gamePlay) => total + (Number(gamePlay['duration']) || 0), 0);
}

// The backend refuses a CTA template whose execution is shorter than its game plays together
// (invalid_duration, CampaignCommons CTATemplate.check_durations) and stops validating there, which
// hides every other error of that round. The fix is usually to lengthen the execution, so the AI flow
// makes it instead of asking the user. An empty duration stays empty: the backend skips it.
// Never lengthened past the campaign end: that only trades invalid_duration for timestamp_out_of_range,
// and the user's shorter value would be overwritten again on every validate. When the game plays don't
// fit, the remaining invalid_duration sends the user to shorten them (buildBriefStops).
export function fitExecutionDurationsToGamePlays(campaign: Record<string, unknown>): Record<string, unknown> {
  const campaignStart = parseDate(campaign['start']);
  const campaignEnd = parseDate(campaign['end']);
  const ctaTemplates = ((campaign['ctaTemplates'] as Record<string, unknown>[]) ?? []).map((ctaTemplate) => {
    const gamePlaysDuration = getGamePlaysDuration(ctaTemplate);
    const executions = (ctaTemplate['executions'] as Record<string, unknown>[] | undefined)?.map((execution) => {
      const duration = Number(execution['duration']);

      if (!duration || duration >= gamePlaysDuration) {
        return execution;
      }

      const room = getMinutesUntilCampaignEnd(execution, campaignStart, campaignEnd);
      const fitted = room === null ? gamePlaysDuration : Math.min(gamePlaysDuration, room);

      return fitted > duration ? { ...execution, duration: fitted } : execution;
    });

    return executions ? { ...ctaTemplate, executions } : ctaTemplate;
  });

  return { ...campaign, ctaTemplates };
}

// Whole minutes from the execution's start to the campaign end, mirroring the backend's execution_window
// rule (CampaignManagementAPI campaign_rules.execution_start): execution_time is the moment, an
// execution_date alone takes the campaign start's time of day, neither starts with the campaign.
// null = no limit: the backend skips the check without both campaign dates, and a time_frame execution
// is checked by its own window, not its duration.
function getMinutesUntilCampaignEnd(
  execution: Record<string, unknown>,
  campaignStart: Date | null,
  campaignEnd: Date | null,
): number | null {
  if (!campaignStart || !campaignEnd || execution['executionType'] === EExecutionType.timeFrame) {
    return null;
  }

  const executionTime = parseDate(execution['executionTime']);
  const executionDate = parseDate(execution['executionDate']);
  let start = campaignStart;

  if (executionTime) {
    start = executionTime;
  } else if (executionDate) {
    start = new Date(
      Date.UTC(
        executionDate.getUTCFullYear(),
        executionDate.getUTCMonth(),
        executionDate.getUTCDate(),
        campaignStart.getUTCHours(),
        campaignStart.getUTCMinutes(),
        campaignStart.getUTCSeconds(),
        campaignStart.getUTCMilliseconds(),
      ),
    );
  }

  return Math.floor((campaignEnd.getTime() - start.getTime()) / MILLISECONDS_IN_A_MINUTE);
}

export function isDeliverableArrayKey(key: string): boolean {
  return !!DELIVERABLE_KEY_TYPE_MAP[key] && !NON_DELIVERABLE_CTA_TEMPLATE_KEYS.has(key);
}

export function deliverableTypeForArrayKey(key: string): EDeliverableType | null {
  return DELIVERABLE_KEY_TYPE_MAP[key] ?? null;
}

// A brief error's deliverable path indexes into the ORIGINAL per-type array (e.g.
// "gamePlays[0]"), but the wizard's working draft has already flattened every type into one
// `deliverables` array (flattenCtaTemplateDeliverables). Since flattening preserves each type's
// original relative order, "the Nth item of this type" still resolves correctly - this finds
// that item's actual index in the flat array.
export function findDeliverableIndexInFlatArray(
  deliverables: Record<string, unknown>[],
  arrayKey: string,
  itemIndexWithinType: number,
): number {
  const type = deliverableTypeForArrayKey(arrayKey);
  let seen = 0;

  for (let index = 0; index < deliverables.length; index += 1) {
    if (deliverables[index]['type'] !== type) {
      continue;
    }

    if (seen === itemIndexWithinType) {
      return index;
    }

    seen += 1;
  }

  return -1;
}

// Converts one wizard-edited cta template (flattened `deliverables`, camelCase `platformFeature`)
// into the shape the real /campaigns create endpoint expects (ICtaTemplateApi): per-deliverable-
// type arrays, and platformFeature back in PascalCase. The PascalCase conversion mirrors
// campaign-form-payload.service.ts's getMappedCtaTemplates exactly
// (`startCase(cta.platformFeature || '').replace(/\s+/g, '')`) so a brief-created campaign's CTA
// templates are encoded identically to ones saved from the real campaign-form.
export function toCtaTemplateApiPayload(ctaTemplate: Record<string, unknown>): Record<string, unknown> {
  const unflattened = unflattenCtaTemplateDeliverables(ctaTemplate);
  const { platformFeature } = unflattened;

  return {
    ...unflattened,
    platformFeature: typeof platformFeature === 'string' ? startCase(platformFeature).replace(/\s+/g, '') : null,
  };
}

// The reverse of denormalizeCampaign's ctaTemplates mapping, but for the /campaigns create
// endpoint rather than /brief/validate - see toCtaTemplateApiPayload.
export function toCampaignApiPayload(campaign: Record<string, unknown>): Record<string, unknown> {
  const ctaTemplates = ((campaign['ctaTemplates'] as Record<string, unknown>[]) ?? []).map(toCtaTemplateApiPayload);

  return { ...campaign, ctaTemplates };
}
